import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, MapState } from "@b2bportal/core-types";
import {
  toHotelCoordinatesType,
  toMapCoordinatesType,
  useDeviceType,
} from "@b2bportal/core-utilities";

import {
  LocationDescriptorEnum,
  LodgingSelectionEnum,
  Platform,
  type Lodging,
  type LodgingPrice,
} from "@b2bportal/lodging-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useGetPriceDisplay } from "@tenant/ui";
import {
  HotelsAvailabilityActions,
  HotelsAvailabilitySelectors,
  fetchHotels,
} from "../../store";
import type { HotelAvailabilityMapProps } from "./HotelAvailabilityMap";

export const useHotelAvailabilityMap = (props: HotelAvailabilityMapProps) => {
  const {
    hotels,
    centroid,
    hoveredId,
    zoom,
    gestureHandling,
    useClusters = true,
    markerLimit,
  } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useI18nContext();

  const { isDesktopAndUp } = useDeviceType();

  const search = useSelector(HotelsAvailabilitySelectors.getSearch);
  const boundingBox = useSelector(
    HotelsAvailabilitySelectors.getMapBoundingBox
  );
  const searchBoundingBox = useSelector(
    HotelsAvailabilitySelectors.getSearchBoundingBox
  );

  const [showSearch, setShowSearch] = useState(false);

  const { getPriceDisplay } = useGetPriceDisplay();
  const points = useMemo(() => {
    if (search != null) {
      return hotels
        .filter((hotel) => hotel.price != null)
        .map((hotel: Lodging & { price: LodgingPrice }) => {
          return {
            data: {
              lodging: hotel,
              dateRange: search.dateRange,
              rooms: search.rooms,
            },
            label: getPriceDisplay({
              prices: hotel.price.nightlyDiscountAware.priceWithAllDiscounts,
            }).priceText,
            id: hotel.lodging.id,
            coordinates: toMapCoordinatesType(
              hotel.lodging.location.coordinates
            ),
          };
        });
    } else {
      return [];
    }
  }, [getPriceDisplay, hotels, search]);

  const handleMapChange = useCallback(
    (mapState: MapState) => {
      setShowSearch(true);
      dispatch(HotelsAvailabilityActions.setMapData(mapState));
    },
    [dispatch]
  );

  return {
    context: {
      points,
      centroid,
      searchBoundingBox,
      hoveredId,
      zoom,
      loadBoundingBox: boundingBox == null,
      gestureHandling,
      useClusters,
      markerLimit,
    },
    handlers: {
      handleMapChange,
      handleMapSearch:
        search != null && boundingBox != null && showSearch
          ? () => {
              setShowSearch(false);
              dispatch(
                HotelsAvailabilityActions.setRequestedSearch({
                  ...search,
                  searchType: LodgingSelectionEnum.Location,
                })
              );
              dispatch(
                fetchHotels({
                  LodgingSelection: LodgingSelectionEnum.Location,
                  request: {
                    stayDates: search.dateRange,
                    guests: search.guests,
                    rooms: search.rooms,
                    platform: isDesktopAndUp
                      ? Platform.Desktop
                      : Platform.Mobile,
                    location: {
                      descriptor: {
                        northEast: toHotelCoordinatesType(
                          boundingBox.northEast
                        ),
                        southWest: toHotelCoordinatesType(
                          boundingBox.southWest
                        ),
                        LocationDescriptor: LocationDescriptorEnum.BoundingBox,
                      },
                      LodgingSelection: LodgingSelectionEnum.Location,
                    },
                  },
                })
              );
            }
          : undefined,
    },

    text: {
      searchMap: t("core-hotels.map.searchThisArea"),
    },
  };
};
